import OpenSeadragon, { TiledImage, TileSource } from "openseadragon";
import React from "react";
import ReactDOM from "react-dom";

import { PrecomputedDZI, ViewerMode } from "custom-models/ViewerTypes";

import { RangeManifest } from "shared/reducers/slideTilesReducer";
import {
  bookmarkUrl,
  parseCachedHash,
} from "shared/utils/openseadragon/bookmark_url";
import ExtendedSeadragon, {
  ExtendedOptions,
  ExtendedSeadragonViewer,
} from "shared/utils/openseadragon/ExtendedSeadragon";

import SvgOverlay from "./subtree/SvgOverlay";
import ViewerFacade from "./ViewerFacade";

export default class OSDClientEffectiveMagnification {
  public high_power_dzi_url: string;
  public low_power_tile_url: string | undefined;
  public low_power_dzi: PrecomputedDZI | undefined;
  public full_precompute_location?: string | null;
  public range_manifest: RangeManifest | undefined;
  public effectiveMagnification: number;

  public slide_rotation_degrees: number;
  public viewer: ExtendedSeadragonViewer | null;
  private viewerFacade: ViewerFacade | null;
  private viewerGoHome: boolean;
  private viewerMode: ViewerMode;
  private div: HTMLElement | null;
  private defaultZoom: number;
  private zooms: number[];

  constructor(
    slide_id: number,
    high_power_dzi_url: string,
    low_power_tile_url: string | undefined,
    low_power_dzi: PrecomputedDZI | undefined,
    full_precompute_location: string | null | undefined,
    range_manifest: RangeManifest | undefined,
    effectiveMagnification: number,
    pixelsPerMeter: number | undefined,
    slide_rotation_degrees: number | null,
    dispatchChangeViewerMode: Function,
    dispatchTilesLoaded: Function,
    setZoom?: Function,
    setRotate?: Function,
    aiOverlay?: boolean,
    landscape_rotation_fix?: boolean
  ) {
    this.high_power_dzi_url = high_power_dzi_url;
    this.low_power_tile_url = low_power_tile_url;
    this.low_power_dzi = low_power_dzi;

    this.slide_rotation_degrees =
      slide_rotation_degrees ??
      (landscape_rotation_fix &&
        range_manifest &&
        range_manifest.Size.Height > range_manifest.Size.Width)
        ? 90
        : 0;
    this.div = document.createElement("div");
    this.range_manifest = range_manifest;
    this.full_precompute_location = full_precompute_location;
    this.effectiveMagnification = effectiveMagnification;
    this.defaultZoom =
      effectiveMagnification >= 80
        ? 1
        : effectiveMagnification >= 60
        ? 0.5
        : 0.25;
    this.zooms = [80, 60, 40, 20, 10, 4, 2, 1, 0.5, 0.25].filter(
      (z) => z <= this.effectiveMagnification
    );

    // Check if everything is available for a rangeBasedPrecompute
    const usingRangeBasedPrecompute =
      full_precompute_location && range_manifest;

    const options: ExtendedOptions = {
      id: "viewer",
      degrees: this.slide_rotation_degrees,
      showNavigator: true,

      showNavigationControl: false,
      showZoomControl: false,
      showHomeControl: false,
      showFullPageControl: false,
      showRotationControl: false,
      showFlipControl: false,
      showSequenceControl: false,

      rotationIncrement: 10,

      zoomPerScroll: 2.0,
      debugMode: false,
      preload: true,

      autoHideControls: false,
      smoothTileEdgesMinZoom: Infinity,

      pixelsPerMeter,

      sequenceMode: true,
    };

    options.navigatorBackground = "#f9f9f9";
    options.navigatorBorderColor = "#cccccc";
    options.navigatorDisplayRegionColor = "var(--teal)";
    // The below used to be styles of .MiniMap2 and #minimap2
    options.navigatorHeight = "128px";
    options.navigatorWidth = "248px";
    options.navigatorPosition = "BOTTOM_LEFT";
    options.navigatorAutoFade = false;
    options.navigatorOpacity = 1;

    options.zoomPerScroll = 2.0;
    options.zoomPerClick = 2.0;
    options.visibilityRatio = 0.0;

    options.defaultZoomLevel = 0; // Taken care of by "open" event
    //@ts-ignore - We implement a minimum zoom based on magnification
    options.minZoomLevel = null;
    //@ts-ignore - We implement a minimum zoom based on magnification
    options.maxZoomLevel = null;
    options.minScrollDeltaTime = 0.0;
    options.zoomPerSecond = 0.0;
    // options.animationTime = 0.0;
    options.gestureSettingsMouse = {
      clickToZoom: false, // Greg asked for single-click zooming;
      dblClickToZoom: true, // hopefully he likes double-click zooming better.
    };
    options.minScrollDeltaTime = 150; // 3X default
    options.pixelsPerWheelLine = 80; // 2X default
    // This would throttle down to 10 concurrent requests
    // options.imageLoaderLimit = 10;
    options.gestureSettingsTouch = {
      pinchToZoom: false,
    };

    // For magnification based zoom we want to allow "zoom out" infinitely
    options.minZoomImageRatio = 0;

    // If we are using a range-based precompute, we must `loadTilesWithAjax` to
    // ... include the `{ range }` Header in requests. If not, we don't want to
    // ... enable this because it causes CORS issues w/ our existing tileserver
    options.loadTilesWithAjax = Boolean(usingRangeBasedPrecompute);

    // This is needed to send cookies with range-based precompute. Without this,
    // our cookies for authentication against cloudfront aren't affixed.
    options.ajaxWithCredentials = Boolean(usingRangeBasedPrecompute);

    options.effectiveMagnification = this.effectiveMagnification;

    this.viewer = ExtendedSeadragon(options);
    this.viewerGoHome = false;

    this.viewerMode = ViewerMode.PAN_ZOOM;

    const viewer = this.viewer;

    this.viewerFacade = new ViewerFacade(viewer, (mode: ViewerMode) => {
      this.setViewerMode(mode);
      dispatchChangeViewerMode(mode);
    });
    const viewerFacade = this.viewerFacade;

    viewer.addHandler("open", () => {
      console.log(`Using OpenSeadragon ${OpenSeadragon.version.versionStr}`);

      // Clears OSD TileCache: viewer.world.resetItems();
      const tiledImage: TiledImage = viewer.world.getItemAt(0);
      /*
            tiledImage.addHandler('bounds-change', (event) => {
                console.log('TiledImageEvent: bounds-change', event);
            });
            tiledImage.addHandler('clip-change', (event) => {
                console.log('TiledImageEvent: clip-change', event);
            });
            tiledImage.addHandler('composite-operation-change', (event) => {
                console.log('TiledImageEvent: composite-operation-change', event);
            })
             */
      tiledImage.addHandler("fully-loaded-change", (event) => {
        setTimeout(() => {
          dispatchTilesLoaded(event.fullyLoaded);
        }, 0);
      });
      // @ts-ignore  Exists for backward compatibility; "not an official part of the API and should not be relied upon."
      viewer.source.minLevel = 8;
      /* Start zoomed in, then return to home position after
               loading.  Workaround for blurry viewport on initial
               load (OpenSeadragon #95). */
      // @ts-ignore  Exists for backward compatibility; "not an official part of the API and should not be relied upon."
      // const center = new OpenSeadragon.Point(0.5, 1 / (2 * viewer.source.aspectRatio));
      // viewer.viewport.zoomTo(2, center, true);
      const params = parseCachedHash(slide_id);
      // If there are no fake hash parameters in the URL for zoom, x, and y, set a flag to
      // indicate that a call to the viewer's goHome() method is needed soon
      this.viewerGoHome = false;
      /* Ensure we receive update-viewport events, OpenSeadragon
               #94 */
      // @ts-ignore  Exists for backward compatibility; "not an official part of the API and should not be relied upon."
      viewer.drawer.viewer = viewer;

      setTimeout(() => {
        // Add SVG overlay
        if (aiOverlay) {
          this.div?.classList.add("osd-overlay-div");
        }
        ReactDOM.render(
          React.createElement(SvgOverlay, { viewerFacade }),
          this.div
        );
        this.div && viewer.addOverlay(this.div);
      }, 10); // Delay to avoid incorrect scaling on the first render
    });

    viewer.addHandler("canvas-drag", (event) => {
      if (this.viewerMode !== ViewerMode.PAN_ZOOM) {
        event.preventDefaultAction = true;
      }
    });

    viewer.addHandler("canvas-click", (event) => {
      if (this.viewerMode !== ViewerMode.PAN_ZOOM) {
        event.preventDefaultAction = true;
      }
    });

    let startingPinchDistance: number | undefined = undefined;

    viewer.addHandler("canvas-release", (_event) => {
      startingPinchDistance = undefined;
    });

    viewer.addHandler("canvas-pinch", (event) => {
      if (this.viewerMode !== ViewerMode.PAN_ZOOM) {
        event.preventDefaultPanAction = true;
      } else {
        // @ts-ignore
        const pinchDistance = event.distance;
        if (startingPinchDistance === undefined) {
          startingPinchDistance = pinchDistance;
        } else if (pinchDistance - startingPinchDistance > 50) {
          this.doZoomOut();
          startingPinchDistance = pinchDistance;
        } else if (startingPinchDistance - pinchDistance > 50) {
          this.doZoomIn();
          startingPinchDistance = pinchDistance;
        }
      }
    });

    viewer.addHandler("canvas-key", function (e: any) {
      e.preventDefault = () => {}; // disable default keyboard controls
      e.preventDefaultAction = true;
      e.preventVerticalPan = true; // disable vertical panning with arrows and W or S keys
      e.preventHorizontalPan = true; // disable horizontal panning with arrows and A or D keys
    });

    let imageZoom = 0;
    let viewportZoom = 0;
    let magnificationLevel = 0;

    viewer.addOnceHandler("open", (e) => {
      console.log("open", e);

      const params = parseCachedHash(slide_id);
      const zoom =
        typeof params.zoom === "undefined"
          ? this.defaultZoom
          : Number(params.zoom);

      viewer.viewport.zoomTo(
        viewer.viewport.imageToViewportZoom(zoom / this.effectiveMagnification),
        undefined,
        true
      );
    });

    viewer.addHandler("zoom", (e) => {
      console.debug(
        "Zoom event is going to: ",
        e.zoom,
        viewer.viewport.viewportToImageZoom(e.zoom)
      );

      if (e.zoom !== viewportZoom && e.zoom !== undefined && e.zoom > 0) {
        console.debug("Previous Zoom: ", {
          viewportZoom,
          imageZoom,
          magnificationLevel,
        });

        viewportZoom = e.zoom;
        imageZoom = viewer.viewport.viewportToImageZoom(viewportZoom);
        magnificationLevel = this.effectiveMagnification * imageZoom;

        console.debug("Preparing to set zoom: ", {
          viewportZoom,
          imageZoom,
          magnificationLevel,
        });

        if (imageZoom >= 1) {
          console.log("imageZoom over 1", imageZoom);

          // Don't allow "over-zooming"
          imageZoom = 1;
          magnificationLevel = this.effectiveMagnification * imageZoom;
          if (magnificationLevel > this.zooms[0]) {
            magnificationLevel = this.zooms[0];
            imageZoom = magnificationLevel / this.effectiveMagnification;
            // TODO: Consider if we want to "pretend" 41.xxx is 40x
            // For now, let's be accurate.
          }
          viewportZoom = viewer.viewport.imageToViewportZoom(imageZoom);

          console.debug("Not allowing over-zooming: ", {
            viewportZoom,
            imageZoom,
            magnificationLevel,
          });
          setZoom && setZoom(magnificationLevel);
          viewer.viewport.zoomTo(viewportZoom, e.refPoint, true);
          return;
        }

        if (magnificationLevel <= this.defaultZoom) {
          magnificationLevel = this.defaultZoom;
          imageZoom = magnificationLevel / this.effectiveMagnification;
          viewportZoom = viewer.viewport.imageToViewportZoom(imageZoom);

          console.debug("Not allowing under-zooming: ", {
            viewportZoom,
            imageZoom,
            magnificationLevel,
          });
          setZoom && setZoom(magnificationLevel);
          viewer.viewport.zoomTo(viewportZoom, e.refPoint, true);
          return;
        }

        // Stick on familiar zoom-points
        if (
          Number(magnificationLevel.toFixed(2)) > 5 &&
          magnificationLevel < 10
        ) {
          magnificationLevel = 10;
          imageZoom = magnificationLevel / this.effectiveMagnification;
          viewportZoom = viewer.viewport.imageToViewportZoom(imageZoom);

          console.debug("Sticking to 10: ", {
            viewportZoom,
            imageZoom,
            magnificationLevel,
          });
        } else if (Number(magnificationLevel.toFixed(2)) === 5) {
          magnificationLevel = 4;
          imageZoom = magnificationLevel / this.effectiveMagnification;
          viewportZoom = viewer.viewport.imageToViewportZoom(imageZoom);

          console.debug("Sticking to 10: ", {
            viewportZoom,
            imageZoom,
            magnificationLevel,
          });
        }

        setZoom && setZoom(magnificationLevel);
        viewer.viewport.zoomTo(viewportZoom, e.refPoint, true);
      } else {
        console.debug("Zoom the same: ", {
          viewportZoom,
          imageZoom,
          magnificationLevel,
        });

        return false;
      }
      return false;
    });

    // TODO: Leaving this for future investigation.
    // ... Instead of hijacking `zoom` event, we could hijack scroll
    // ... This would nessessitate more investigation around the other mouse/touch/points events, too
    /*
    viewer.addHandler("canvas-scroll", (e) => {
      const currentImageZoom = viewer.viewport.viewportToImageZoom(
        viewer.viewport.getZoom()
      );

      if (
        options.zoomPerScroll &&
        ((e.scroll === 1 && currentImageZoom * options.zoomPerScroll > 1) ||
          (e.scroll === -1 &&
            this.effectiveMagnification &&
            (currentImageZoom * this.effectiveMagnification) /
              options.zoomPerScroll <
              DEFAULT_ZOOM))
      ) {
        console.debug(
          "Stopping canvas-scroll",
          e.scroll,
          currentImageZoom,
          currentImageZoom * this.effectiveMagnification
        );
        e.preventDefaultAction = true;
      }
    });
    */

    viewer.addHandler("rotate", (e) => {
      if (setRotate) {
        setRotate(e.degrees);
      }
    });

    viewer.addHandler("canvas-click", (e) => {
      // Clicking to centre the viewport
      if (this.viewerMode === ViewerMode.PAN_ZOOM && e.position !== undefined) {
        const webPoint = e.position;
        const viewportPoint = viewer.viewport.pointFromPixel(webPoint);
        // if (e.quick) // quick will prevent a single left click from triggering when dragging
        // Single left click still does interfere with other functions, so try a modifier
        if (e.shift) viewer.viewport.panTo(viewportPoint, true);
      }
    });

    viewer.addHandler("canvas-nonprimary-release", (e) => {
      // Right click to center the viewport
      if (e.button === 2 && e.position !== undefined) {
        const webPoint = e.position;
        const viewportPoint = viewer.viewport.pointFromPixel(webPoint);
        viewer.viewport.panTo(viewportPoint, true);
      }
    });
    viewer.element.addEventListener("contextmenu", (e: Event) => {
      e.preventDefault();
    });

    bookmarkUrl(viewer, slide_id);

    const stripExtension = (str: string) => {
      const matches = str.match(/(.*)(\.\w+)/);
      return matches ? matches[1] : str;
    };

    const high_power_dzi_base = stripExtension(this.high_power_dzi_url);

    if (usingRangeBasedPrecompute) {
      const precomputed_dzij_base = stripExtension(full_precompute_location);
      const tileSource = new TileSource({
        width: range_manifest.Size.Width,
        height: range_manifest.Size.Height,
        tileSize: range_manifest.TileSize,
        tileOverlap: range_manifest.Overlap,

        // @ts-ignore (this is a legitimate property on TileSource)
        getTileAjaxHeaders: (level: number, x: number, y: number) => {
          const range = range_manifest.Levels[level][`${x}_${y}`];
          return { Range: `bytes=${range}` };
        },

        getTileUrl: (level: number, x: number, y: number) => {
          // We want to append a querystring to the bundle file such that Chrome does not
          // ERR_CACHE_LOCK_TIMEOUT against requesting the same URL over and over again
          return `${precomputed_dzij_base}_files/${level}.${range_manifest.Format}bundle?${x}_${y}`;
        },
      });

      viewerFacade.setWsiDWidth(range_manifest.Original_Size.Width);
      viewerFacade.setWsiHeight(range_manifest.Original_Size.Height);
      viewerFacade.setDziOffset({
        x: range_manifest.Offset.Width,
        y: range_manifest.Offset.Height,
      });
      viewer.open(tileSource);
      return;
    }

    if (low_power_dzi && low_power_tile_url) {
      // Newer slides have low_power_dzi with a PrecomputedDZI object along with a low_power_tile_url.
      const tileSource = new TileSource({
        width: low_power_dzi.width,
        height: low_power_dzi.height,
        tileSize: low_power_dzi.tile_size,
        tileOverlap: low_power_dzi.overlap,
        getTileUrl: (level: number, x: number, y: number) => {
          const base =
            level < low_power_dzi.pyramid_levels
              ? low_power_tile_url
              : high_power_dzi_base;
          const url =
            base && `${base}_files/${level}/${x}_${y}.${low_power_dzi.format}`;
          return url;
        },
      });

      viewerFacade.setWsiDWidth(low_power_dzi.original_width);
      viewerFacade.setWsiHeight(low_power_dzi.original_height);
      viewerFacade.setDziOffset({
        x: low_power_dzi.offset_width,
        y: low_power_dzi.offset_height,
      });
      viewer.open(tileSource);
      return;
    }
    // OLDER SLIDES
    window
      .fetch(high_power_dzi_url)
      .then((response) => response.text())
      .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
      .then((xmlDoc) => {
        const parseAttr = (el: Element | null | undefined, attrname: string) =>
          parseInt(el?.getAttribute(attrname) || "0", 10);
        const documentElement: Element = xmlDoc.documentElement;
        const tileSize = parseAttr(documentElement, "TileSize");
        const tileOverlap = parseAttr(documentElement, "Overlap");
        const format = documentElement.getAttribute("Format");

        const byTagName = new Map<String, Element>();
        for (let i = 0; i < documentElement.children.length; i++) {
          byTagName.set(
            documentElement.children[i].tagName,
            documentElement.children[i]
          );
        }

        const size = byTagName.get("Size");
        const width = parseAttr(size, "Width");
        const height = parseAttr(size, "Height");
        const tileSource: TileSource = new TileSource({
          width,
          height,
          tileSize,
          tileOverlap,
          getTileUrl: (level: number, x: number, y: number) => {
            return `${high_power_dzi_base}_files/${level}/${x}_${y}.${format}`;
          },
        });

        const originalSize = byTagName.get("Original_Size");
        if (originalSize) {
          const wsiWidth: number = parseAttr(originalSize, "Width");
          const wsiHeight: number = parseAttr(originalSize, "Height");
          viewerFacade.setWsiDWidth(wsiWidth);
          viewerFacade.setWsiHeight(wsiHeight);
        }

        const offset = byTagName.get("Offset");
        if (offset) {
          const offsetX = parseAttr(offset, "Width");
          const offsetY = parseAttr(offset, "Height");
          viewerFacade.setDziOffset({ x: offsetX, y: offsetY });
        }

        viewer.open(tileSource);
      })
      .catch((e) => {
        console.log(
          `Could not parse XML at ${high_power_dzi_url} : ${e.message}`
        );
        viewer.open(high_power_dzi_url, 0); // prior behavior
      });
  }

  public doZoomIn = () => {
    if (!this.viewer) return;
    const currZoom = Number(
      (
        this.viewer.viewport.viewportToImageZoom(
          this.viewer.viewport.getZoom()
        ) * this.effectiveMagnification
      ).toFixed(2)
    );
    let lockZoom = 0;
    for (let i = this.zooms.length - 1; i >= 0; i--) {
      if (this.zooms[i] > currZoom) {
        lockZoom = this.zooms[i];
        break;
      }
    }
    if (lockZoom === 0) {
      lockZoom = this.zooms[0];
    }
    this.viewer.viewport.zoomTo(
      this.viewer.viewport.imageToViewportZoom(
        lockZoom / this.effectiveMagnification
      )
    );
    this.viewer.viewport.applyConstraints();
  };

  public doZoomOut() {
    if (!this.viewer) return;
    const currZoom = Number(
      (
        this.viewer.viewport.viewportToImageZoom(
          this.viewer.viewport.getZoom()
        ) * this.effectiveMagnification
      ).toFixed(2)
    );
    let lockZoom = 0;
    for (let i = 0; i < this.zooms.length; i++) {
      if (this.zooms[i] < currZoom) {
        lockZoom = this.zooms[i];
        break;
      }
    }
    if (lockZoom === 0) {
      lockZoom = this.zooms[this.zooms.length - 1];
    }
    this.viewer.viewport.zoomTo(
      this.viewer.viewport.imageToViewportZoom(
        lockZoom / this.effectiveMagnification
      )
    );
    this.viewer.viewport.applyConstraints();
  }

  doZoomMin() {
    if (!this.viewer) return;

    this.viewer.viewport.zoomTo(
      this.viewer.viewport.imageToViewportZoom(
        (this.zooms.at(-1) || this.defaultZoom) / this.effectiveMagnification
      ),
      undefined,
      true
    );
  }

  doRotate(angle: number) {
    if (!this.viewer) return;
    let rotation = this.viewer.viewport.getRotation();
    rotation += angle;
    if (rotation < 0) {
      rotation += 360;
    } else if (rotation > 360) {
      rotation -= 360;
    }
    // @ts-expect-error: latest @types for OSD does not include "immediately" paramter to setRotation
    this.viewer.viewport.setRotation(rotation, true);
  }

  doHome() {
    if (!this.viewer) return;
    this.viewer.viewport.goHome();
    this.doZoomMin();
    this.viewer.viewport.setRotation(
      this.slide_rotation_degrees ? this.slide_rotation_degrees : 0,
      // @ts-expect-error: latest @types for OSD does not include "immediately" paramter to setRotation
      true
    );
  }

  public setViewerMode(mode: ViewerMode) {
    if (!this.viewer) return;
    this.viewerMode = mode;
    // Greg asked for clickToZoom to be set to true, but it apparently causes him problems in drawing mode
    const gestureSettings = this.viewer.gestureSettingsByDeviceType("mouse");
    if (mode === ViewerMode.PAN_ZOOM) {
      gestureSettings.clickToZoom = false;
      gestureSettings.dblClickToZoom = true;
    } else {
      gestureSettings.clickToZoom = false;
      gestureSettings.dblClickToZoom = false;
    }
  }

  public setDebugMode(debug: boolean) {
    // @ts-ignore  setDebugMode not declared
    this.viewer.viewport.setDebugMode(debug);
  }

  public destroy() {
    /*
        Sentry occasionally reports "TypeError: undefined is not an object (evaluating 't[u.hash].forceRedraw=!0')"
        which is apparently thrown from line 10384 in openseadragon.js.
        Ref: https://sentry.io/organizations/pathology-watch/issues/2221365540/?project=5191312
        I reproduced this 2 or 3 times so far and haven't notice any visible effect.
        MIGHT be related to reported Github issue: "Navigator in Custom Location - Exception Thrown on destroy()"
         */
    console.debug("Destroying Viewer");
    this.viewerFacade?.destroy();
    this.viewerFacade = null;
    this.viewer?.destroy();
    this.viewer = null; // important
    this.div && ReactDOM.unmountComponentAtNode(this.div);
    this.div?.remove();
    this.div = null;
  }
}
